import { Button, ListBox } from '@dev-spendesk/grapes';
import { useState } from 'react';
import { useDispatch } from 'react-redux';

import { useFeature } from 'common/hooks/useFeature';
import { useWalletTransactionForecastQuery } from 'modules/company/hooks';
import { PaymentContext } from 'modules/invoices/hooks';
import { useModalState } from 'src/core/common/hooks/useModalState';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import FEATURES from 'src/core/constants/features';
import { InvoiceInconsistencyItemSummary } from 'src/core/modules/invoices/components/InvoiceInconsistencyItemSummary';
import { useVerificationOfPayeeQuery } from 'src/core/modules/verification-of-payee/apis';
import { ConfirmInconsistencyModal } from 'src/core/modules/verification-of-payee/components/ConfirmInconsistencyModal';
import type { InvoiceInconsistencyItem } from 'src/core/modules/verification-of-payee/models';
import { AnalyticEventName, track } from 'src/core/utils/analytics';

import { TransferListHeader } from './TransferListHeader';
import { TransferListRow } from './TransferListRow';
import {
  useConfirmTransfersTrackingEvents,
  useVerificationOfPayeeInconsistencyItems,
} from '../../hooks';
import {
  type Transfer,
  type TransferId,
  walletAmountAllocationToCheckForMissingFunds,
} from '../../models';
import { actions, selectors, useSelectorWithContext } from '../../redux';
import { TransferCancelModal } from '../TransferCancelModal';
import { TransferConfirmationModal } from '../TransferConfirmationModal';

import './TransferList.css';

type Props = {
  activeTransferId: TransferId | undefined;
  transfers: Transfer[];
  hasNextPage: boolean;
  totalTransfersCount: number;
  canApproveTransfers: boolean;
  onRowClick(transfer: Transfer): void;
  fetchNextPage(): void;
};

export const TransferList = ({
  activeTransferId,
  transfers,
  hasNextPage,
  canApproveTransfers,
  onRowClick,
  fetchNextPage,
}: Props) => {
  const { t } = useTranslation('global');
  const { type } = PaymentContext.useContext();
  const confirmTransfersTrackingEvents = useConfirmTransfersTrackingEvents();
  const dispatch = useDispatch();
  const getWalletTransactionForecastQuery = useWalletTransactionForecastQuery();
  const transfersSelected = useSelectorWithContext(
    selectors.selectTransfersSelected,
  );
  const transfersSelectedCount = useSelectorWithContext(
    selectors.selectTransfersSelectedCount,
  );
  const invalidVoPTransfers = useSelectorWithContext(
    selectors.selectTransfersWithInvalidVerificationOfPayee,
  );
  const verificationOfPayeeInconsistencyItems =
    useVerificationOfPayeeInconsistencyItems(invalidVoPTransfers);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const confirmationModalState = useModalState({
    onOpen: () => {
      confirmTransfersTrackingEvents.onConfirmTransfersConfirmationModalOpened({
        transfers: transfersSelected,
      });
    },
    onClose: () => {
      confirmTransfersTrackingEvents.onConfirmTransfersConfirmationModalClosed({
        transfers: transfersSelected,
      });
    },
  });
  const cancelModalState = useModalState({
    onOpen: () => {
      confirmTransfersTrackingEvents.onConfirmTransfersRejectionModalOpened({
        transfers: transfersSelected,
      });
    },
    onClose: () => {
      confirmTransfersTrackingEvents.onConfirmTransfersRejectionModalClosed({
        transfers: transfersSelected,
      });
    },
  });
  const inconsistencyModalState = useModalState({});
  const isVerificationOfPayeeEnabled = useFeature(
    FEATURES.TMP_VERIFICATION_OF_PAYEE_EEA,
  );
  const { mutateAsync: checkVerificationOfPayee } =
    useVerificationOfPayeeQuery();

  const openConfirmationModal = () => {
    confirmationModalState.open();
    setIsLoading(false);
  };

  const onConfirmTransfer = async (transfer: Transfer) => {
    setIsLoading(true);
    dispatch(
      actions.setTransfersSelected({
        transfersSelected: [transfer],
        type,
      }),
    );

    if (isVerificationOfPayeeEnabled) {
      const counterparty = transfer.bill.counterparty;

      if (
        !counterparty.bankAddress?.iban ||
        !counterparty.bankAddress?.accountHolderName
      ) {
        return;
      }

      const verificationOfPayeeResult = await checkVerificationOfPayee({
        iban: counterparty.bankAddress.iban,
        name: counterparty.bankAddress.accountHolderName,
        refresh: true,
      });

      dispatch(
        actions.updateTransferVerificationOfPayee({
          transfers: [
            {
              transferId: transfer.id,
              verificationOfPayee: {
                initialResult: verificationOfPayeeResult,
                isIgnored: false,
              },
            },
          ],
          type,
        }),
      );

      if (verificationOfPayeeResult.matchingResult === 'match') {
        openConfirmationModal();
        return;
      }

      inconsistencyModalState.open();
      return;
    }
    openConfirmationModal();
  };

  const onCancelTransfer = (transfer: Transfer) => {
    dispatch(
      actions.setTransfersSelected({
        transfersSelected: [transfer],
        type,
      }),
    );
    cancelModalState.open();
  };

  const resetTransfersSelection = () => {
    dispatch(actions.resetTransfersSelection({ type }));
  };

  const onCloseInconsistencyModal = () => {
    inconsistencyModalState.close();
  };

  const onCancelInconsistencyModal = () => {
    inconsistencyModalState.close();
    setIsLoading(false);
    cancelModalState.open();
  };

  const onContinueInconsistencyModal = () => {
    inconsistencyModalState.close();
    setIsLoading(false);
    confirmationModalState.open();
  };

  return (
    <section>
      <ListBox
        className="TransferList__table"
        options={transfers}
        onOptionClick={onRowClick}
        getIsOptionActive={(transfer) => transfer.id === activeTransferId}
        getOptionId={(transfer) => transfer.id}
        checkedOptionIds={transfersSelected.map((transfer) => transfer.id)}
        onOptionChange={(checkedTransfer, _, isChecked) => {
          if (isChecked) {
            track(AnalyticEventName.FINANCE_CONFIRM_TRANSFER_SELECTED, {
              category: 'invoice',
              selectionType: 'single',
            });
          }
          dispatch(
            actions.setTransfersSelected({
              transfersSelected: isChecked
                ? transfersSelected.concat(checkedTransfer)
                : transfersSelected.filter(
                    (transfer) => transfer.id !== checkedTransfer.id,
                  ),
              type,
            }),
          );
        }}
        onAllOptionsChange={(checkedTransfers, _, isChecked) => {
          if (isChecked) {
            track(AnalyticEventName.FINANCE_CONFIRM_TRANSFER_SELECTED, {
              category: 'invoice',
              selectionType: 'select_all',
            });
          }
          dispatch(
            actions.setTransfersSelected({
              transfersSelected: isChecked ? checkedTransfers : [],
              type,
            }),
          );
        }}
        header={
          <TransferListHeader selectedTransfersCount={transfersSelectedCount} />
        }
        footer={
          hasNextPage ? (
            <div className="mx-auto my-16">
              <Button
                variant="secondaryNeutral"
                text={t('misc.loadMore')}
                onClick={fetchNextPage}
              />
            </div>
          ) : undefined
        }
      >
        {(transfer) => (
          <TransferListRow
            transfer={transfer}
            canApproveTransfers={canApproveTransfers}
            onConfirmTransfer={onConfirmTransfer}
            onCancelTransfer={onCancelTransfer}
            getWalletTransactionForecastQueryForTransfer={() =>
              getWalletTransactionForecastQuery(
                transfer.amount,
                walletAmountAllocationToCheckForMissingFunds,
              )
            }
          />
        )}
      </ListBox>
      <ConfirmInconsistencyModal<InvoiceInconsistencyItem>
        isOpen={inconsistencyModalState.isOpen}
        items={verificationOfPayeeInconsistencyItems}
        renderItemSummary={(item) => (
          <InvoiceInconsistencyItemSummary item={item} />
        )}
        labels={{
          titleLabel: 'inconsistencyModal.title',
          descriptionLabel: 'inconsistencyModal.confirm.description',
        }}
        onCancel={onCancelInconsistencyModal}
        onContinue={onContinueInconsistencyModal}
        onClose={onCloseInconsistencyModal}
      />
      <TransferConfirmationModal
        isOpen={
          confirmationModalState.isOpen && !inconsistencyModalState.isOpen
        }
        onClose={confirmationModalState.close}
        onConfirm={resetTransfersSelection}
        transfers={transfersSelected}
        isLoading={isLoading}
      />
      <TransferCancelModal
        isOpen={cancelModalState.isOpen && !inconsistencyModalState.isOpen}
        onClose={cancelModalState.close}
        onConfirm={resetTransfersSelection}
        transfers={transfersSelected}
      />
    </section>
  );
};
